import {appApi} from "./api.service";
import {getExtension} from "./file.service";
import {Contract} from "../models/contract";
import {ContractStatus} from "../enums/contractStatus";
import moment from "moment";
import {Device} from "@capacitor/device";
import {ContractExitInterviewQuestion} from "../models/contractExitInterview";
import {SecureStoragePlugin} from "capacitor-secure-storage-plugin";
import {offlineStorageGet} from "@services/offlineSync.service";
import {OfflineDataReport} from "@services/reportOffline.service";


let endpointPrefix = 'worker/contracts';

// Cache z promise deduplication - zapobiega wielokrotnym wywołaniom API
let contractsCache: {
    data: any;
    timestamp: number;
    promise: Promise<any> | null;
} = {
    data: null,
    timestamp: 0,
    promise: null
};
const CACHE_TTL = 5000; // 5 sekund

const getContractsCached = async (noLoader: boolean = false): Promise<any> => {
    const now = Date.now();

    // Zwróć cache jeśli świeży
    if (contractsCache.data && (now - contractsCache.timestamp) < CACHE_TTL) {
        return { data: contractsCache.data };
    }

    // Zwróć istniejący promise jeśli request w trakcie (deduplication)
    if (contractsCache.promise) {
        return contractsCache.promise;
    }

    // Nowe żądanie
    const config = noLoader ? { headers: { 'No-Loader': true } } : {};
    contractsCache.promise = appApi.get(endpointPrefix, config)
        .then(response => {
            contractsCache.data = response.data;
            contractsCache.timestamp = Date.now();
            contractsCache.promise = null;
            return response;
        })
        .catch(err => {
            contractsCache.promise = null;
            throw err;
        });

    return contractsCache.promise;
};

// Funkcja do czyszczenia cache (np. po podpisaniu umowy)
const clearContractsCache = () => {
    contractsCache.data = null;
    contractsCache.timestamp = 0;
    contractsCache.promise = null;
};

const getContracts = async () => {
    return getContractsCached(false);
}

const getContractsNoLoader = async () => {
    return getContractsCached(true);
}

const getContractsToSign = async () => {
    return await getContractsCached(true).then(response => {
        let contracts: [] = response?.data ;

        return contracts.filter((contract: Contract) => {
            return contract.sign_status === ContractStatus.WAITING_FOR_WORKER_SIGN || contract.sign_status === ContractStatus.WAITING_FOR_WORKER_SIGN_STATEMENT || contract.sign_status === ContractStatus.WAITING_FOR_DOCUMENTS || contract.sign_status === ContractStatus.SEND_BY_KO;
        });
    });
}

const getContractTisax = async () => {
    let contacts = await getContractsCached(false).then(response => {
        let contracts: [] = response?.data ;

        return contracts.filter((contract: Contract) => {
            return contract.need_tisax_sign;
        });
    });

    return contacts[0];
}

const getForeignTermsSignContract = async () => {
    let contacts = await getContractsCached(false).then(response => {
        let contracts: [] = response?.data ;

        return contracts.filter((contract: Contract) => {
            return contract.need_foreign_terms_sign;
        });
    });

    return contacts[0];
}

const hasUnsignedContracts = async () => {
    return await getContractsToSign().then(r => r.length > 0);
}

const hasTisaxContract = async () => {
    let contract = await getContractTisax();
    return contract !== undefined;
}

const hasForeignTermsSignContract = async () => {
    let contract = await getForeignTermsSignContract();
    return contract !== undefined;
}

const getContract = async (id: bigint | string) => {
    return appApi.get(endpointPrefix + '/' + id);
}

const getContractAgreement = async (id: bigint | string) => {
    return appApi.get(endpointPrefix + '/agreement/' + id);
}

const getContractPdf = async (id: bigint | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/download', {
        responseType: "blob"
    });
}

const getContractCertificatePdf = async (id: bigint | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/download-certificate', {
        responseType: "blob"
    });
}

const getContractRegulationsPdf = async (id: bigint | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/download-regulations', {
        responseType: "blob"
    });
}

const getContractAgreementPdf = async (id: bigint | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/download-agreement', {
        responseType: "blob"
    });
}

const getContractFileBase64 = async (id: bigint | string, fileId: bigint | number | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/contract-system-document/'+ fileId +'/base64-generate');
}

const saveContractComments = async (id: any, comment: string, description: string) => {
    return appApi.post(endpointPrefix + '/' + id + '/comment', {
        'comment': comment,
        'description': description
    });
}

const getContractFolderFileBase64 = async (id: bigint | string, folderId: bigint | number | string, fileId: bigint | number | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/files-folder/' + folderId + '/file/' + fileId + '/base64-generate');
}

const signContract = async (id: any) => {
    return appApi.post(endpointPrefix + '/' + id + '/sign', {
        'rulesAccepted': 1,
        'disclaimerAccepted': 1,
        'contractFormAccepted': 1,
        'tisaxSignedByWorker': 1,
        'foreignTermsAccepted': 1,
        'formOfEmploymentInfoAccepted': 1
    });
}

const signContractVerify = async (id: any, code: string) => {
    let device = await Device.getInfo();

    return appApi.post(endpointPrefix + '/' + id + '/sign/code-verification', {
        'verificationCode': code,
        'deviceName': device.model + ' (' + device.osVersion + ')'
    });
}

const terminateContract = async (id: bigint) => {
    return appApi.post(endpointPrefix + '/' + id + '/terminate', {
        'terminateCheckbox': 1,
    });
}

const terminateContractByAgreement = async (id: any) => {
    return appApi.post(endpointPrefix + '/' + id + '/terminate/by-agreement');
}

const rejectContract = async (id: bigint) => {
    return appApi.post(endpointPrefix + '/' + id + '/reject');
}

const rejectAutoContract = async (id: bigint, reason: string) => {
    return appApi.post(endpointPrefix + '/' + id + '/reject', { reason });
}

const saveAutoContractDocumentsStatement = async (id: bigint) => {
    return appApi.post(endpointPrefix + '/' + id + '/auto-contract-statement/save');
}

const terminateContractVerify = async (id: any, code: string) => {
    return appApi.post(endpointPrefix + '/' + id + '/terminate/code-verification', {
        'verificationCode': code
    });
}

const getContractTerminationPdf = async (id: bigint | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/download-termination', {
        responseType: "blob"
    });
}

const getContractChangeTypePdf = async (id: bigint | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/download-change-type', {
        responseType: "blob"
    });
}

const getContractFolders = async (id: bigint | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/files-folders');
}

const getContractFolderFiles = async (id: bigint | number | string, folderId: bigint | number | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/files-folders/' + folderId);
}

const newContractFolder = async (id: bigint, name: string) => {
    return appApi.post(endpointPrefix + '/' + id + '/files-folders/new', {
        name: name,
    });
}

const removeContractFolder = async (id: bigint | number | string, folderId: bigint | number | string) => {
    return appApi.post(endpointPrefix + '/' + id + '/files-folders/' + folderId + '/remove');
}

const uploadPhoto = async (id: bigint, folderId: bigint, file: Blob | File, name: string) => {
    let formData = new FormData();
    formData.append("files[]", file, name);
    return appApi.post(endpointPrefix + '/' + id + '/files-folders/' + folderId + '/files/upload', formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        }
    });
}

const removePhoto = async (id: bigint | number, folderId: bigint | number, fileId: bigint | number) => {
    return appApi.post(endpointPrefix + '/' + id + '/files-folders/' + folderId + '/files/' + fileId + '/remove');
}

const renamePhoto = async (id: bigint | number, folderId: bigint | number, fileId: bigint | number, name: string, extension: string) => {
    if (getExtension(name) !== extension) {
        name += ('.' + extension);
    }

    return appApi.post(endpointPrefix + '/' + id + '/files-folders/' + folderId + '/files/' + fileId + '/rename', {
        name: name
    });
}

const isStarted = (contract: Contract) => {
    return moment(contract.start_at).isSameOrBefore(moment(), 'd');
}

const documentsStatementGenerate = async (id: bigint, schoolName: string, city: string, schoolYear: string, schoolPeriod: string) => {
    return appApi.post(endpointPrefix + '/' + id + '/documents-statement/generate', {
        'schoolName': schoolName,
        'city': city,
        'schoolYear': schoolYear,
        'schoolPeriod': schoolPeriod
    });
}

const documentsStatementSign = async (id: bigint) => {
    return appApi.post(endpointPrefix + '/' + id + '/documents-statement/sign');
}

const documentsStatementVerify = async (id: any, code: string) => {
    return appApi.post(endpointPrefix + '/' + id + '/documents-statement/sign/code-verification', {
        'verificationCode': code
    });
}

const documentsStatementPdf = async (id: bigint | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/download-documents-statement', {
        responseType: "blob"
    });
}

const saveExitInterview = async (id: bigint, selectedAnswers: any, department?: string, coordinator?: string) => {
    let params = {
            form: {
                department: department,
                coordinator: coordinator,
                question: selectedAnswers
            }
        };

    return appApi.post(endpointPrefix + '/' + id + '/exit-interview', params);
}

export const exitInterviewDepartment = () => {
    return appApi.get(endpointPrefix + '/exit-interview/departments')
}

export const exitInterviewCoordinator = () => {
    return appApi.get(endpointPrefix + '/exit-interview/coordinators')
}

const getAdditionalDocumentBase64 = async (id: bigint | string, fileId: bigint | number | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/additional-document/'+ fileId +'/base64-generate');
}

const getPayrollRegulationsPdf = async (id: bigint | string) => {
    return appApi.get(endpointPrefix + '/' + id + '/payroll-regulations', {
        responseType: "blob"
    });
}

const getActiveContract = async () => {
    return appApi.get(`${endpointPrefix}/active`);
}

export const getActiveContractOffline = async () => {
    let keys = await SecureStoragePlugin.keys();

    if (keys.value.length > 0) {
        for (let key of keys.value.filter(k => k.startsWith('offline_data_'))) {
            let reports:OfflineDataReport[] = await offlineStorageGet(key) as unknown as [];

            if (reports.length > 0) {
                reports.map((posData: OfflineDataReport) => {
                    return posData.activeContract;
                })
            }
        }
    }
}


export {
    getContracts,
    getContractsToSign,
    hasUnsignedContracts,
    getContract,
    getContractPdf,
    getContractCertificatePdf,
    getContractRegulationsPdf,
    getContractAgreement,
    getContractAgreementPdf,
    saveContractComments,
    signContract,
    signContractVerify,
    terminateContract,
    terminateContractVerify,
    terminateContractByAgreement,
    rejectContract,
    rejectAutoContract,
    saveAutoContractDocumentsStatement,
    getContractFolders,
    getContractFolderFiles,
    newContractFolder,
    removeContractFolder,
    uploadPhoto,
    removePhoto,
    renamePhoto,
    getContractTerminationPdf,
    getContractChangeTypePdf,
    isStarted,
    getContractFileBase64,
    documentsStatementGenerate,
    documentsStatementSign,
    documentsStatementVerify,
    documentsStatementPdf,
    getContractTisax,
    hasTisaxContract,
    getForeignTermsSignContract,
    hasForeignTermsSignContract,
    saveExitInterview,
    getAdditionalDocumentBase64,
    getPayrollRegulationsPdf,
    getActiveContract,
    getContractFolderFileBase64,
    clearContractsCache,
};
